import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Download, Image as ImagenIcono, Link2, MessageCircle, RotateCcw, Settings2, StickyNote, X } from 'lucide-react';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { dinero, numero } from '@/lib/formato-tienda';
import { viendoComo } from '@/lib/ver-como';
import { cn } from '@/lib/cn';
import type { Periodo } from '@/api/tienda.api';
import * as api from '@/api/cobros.api';
import type { EstadoDeTicket, MotivoDescarte, Ticket } from '@/api/cobros.api';

/**
 * COBROS — lo que se vio pagar en los chats, hasta que su venta se confirma.
 *
 * Un ticket nace solo cuando una clienta manda el comprobante, dice que pagó o queda en pasar a
 * una hora. Se cierra solo al confirmar su venta; si no, la vendedora lo vincula a una venta ya
 * hecha o lo descarta con motivo. Cada aviso, pregunta y respuesta queda en el historial: arriba,
 * lo facturado y la comisión, a la vista de la dueña y de la administración.
 */

const PESTANAS: Array<{ id: EstadoDeTicket; etiqueta: string }> = [
  { id: 'ABIERTO', etiqueta: 'Sin cerrar' },
  { id: 'CONFIRMADO', etiqueta: 'Confirmados' },
  { id: 'DESCARTADO', etiqueta: 'Descartados' },
];

const PERIODOS: Array<{ id: Periodo | 'todo'; etiqueta: string }> = [
  { id: 'hoy', etiqueta: 'Hoy' },
  { id: 'semana', etiqueta: 'Semana' },
  { id: 'mes', etiqueta: 'Mes' },
  { id: 'todo', etiqueta: 'Todo' },
];

const cuando = (iso: string) =>
  new Date(iso).toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

function Ajustes({ onCerrar }: { onCerrar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['cobros', 'config'], queryFn: api.config });
  const [cambios, setCambios] = useState<Partial<api.Config>>({});
  const v = { ...data, ...cambios } as api.Config;
  const guardar = useMutation({
    mutationFn: () => api.guardarConfig(cambios),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cobros'] });
      toast.success('Ajustes de cobros guardados');
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });
  const campo = 'w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900';
  return (
    <Modal
      open
      onClose={onCerrar}
      title="Ajustes de cobros"
      description="Solo la administración los cambia."
      footer={
        <>
          <Button variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} loading={guardar.isPending} disabled={!data}>
            Guardar
          </Button>
        </>
      }
    >
      {!data ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          <label className="block space-y-1">
            <span className="text-xs text-slate-500">Comisión de Silva Notion (% de lo facturado)</span>
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              className={campo}
              value={v.comisionPorcentaje ?? ''}
              onChange={(e) => setCambios((c) => ({ ...c, comisionPorcentaje: e.target.value === '' ? null : Number(e.target.value) }))}
            />
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={v.leerConIA}
              onChange={(e) => setCambios((c) => ({ ...c, leerConIA: e.target.checked }))}
            />
            <span>
              Leer los comprobantes y los productos con IA
              <span className="block text-xs text-slate-500">
                Saca monto, banco y qué se llevó. Gasta saldo de la clave de IA (menos de US$0,001 por ticket) y respeta el tope diario.
              </span>
            </span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block space-y-1">
              <span className="text-xs text-slate-500">Avisar cada (h)</span>
              <input type="number" min={0.5} max={48} step={0.5} className={campo} value={v.perseguirCadaHoras}
                onChange={(e) => setCambios((c) => ({ ...c, perseguirCadaHoras: Number(e.target.value) }))} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-slate-500">A la dueña a las (h)</span>
              <input type="number" min={1} max={168} className={campo} value={v.escalarTrasHoras}
                onChange={(e) => setCambios((c) => ({ ...c, escalarTrasHoras: Number(e.target.value) }))} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-slate-500">Avisos máximos</span>
              <input type="number" min={0} max={10} className={campo} value={v.maxAvisos}
                onChange={(e) => setCambios((c) => ({ ...c, maxAvisos: Number(e.target.value) }))} />
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Los WhatsApp (a la vendedora, la pregunta y el aviso a la dueña) se encienden en «Avisos», cada uno con su número.
            Apagados, todo queda en esta pantalla.
          </p>
        </div>
      )}
    </Modal>
  );
}

export default function Cobros() {
  const { user } = useAuth();
  const supervisa = user?.role === 'OWNER' || user?.role === 'ADMIN';
  const esAdministracion = user?.role === 'OWNER' && !viendoComo();
  // SUPERFILTRO (Josué, 14-oct): las pestañas de estado y las píldoras de período de antes son
  // facetas, con vendedora, origen y banco; todo en la URL. Las opciones en blanco son lo de por
  // defecto («Sin cerrar» y «Este mes»): el período también manda en las cifras de arriba.
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { estado: 'ABIERTO', periodo: 'mes', vendedora: '', origen: '', banco: '', buscar: '' },
    { estado: PESTANAS.map((p) => p.id), periodo: PERIODOS.map((p) => p.id), origen: Object.keys(api.ORIGEN) },
  );
  const estado = filtros.estado as EstadoDeTicket;
  const periodo = filtros.periodo as Periodo | 'todo';
  const [abierto, setAbierto] = useState<string | null>(null);
  const [descartando, setDescartando] = useState<Ticket | null>(null);
  const [motivo, setMotivo] = useState<MotivoDescarte>('no_pago');
  const [vinculando, setVinculando] = useState<Ticket | null>(null);
  const [anotando, setAnotando] = useState<Ticket | null>(null);
  const [nota, setNota] = useState('');
  const [ajustes, setAjustes] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const resumen = useQuery({ queryKey: ['cobros', 'resumen', periodo], queryFn: () => api.resumen(periodo), enabled: supervisa });
  const lista = useQuery({ queryKey: ['cobros', 'lista', estado, periodo], queryFn: () => api.listar({ estado, periodo }) });
  const ventas = useQuery({
    queryKey: ['cobros', 'ventas', vinculando?.id],
    queryFn: () => api.ventasParaVincular(vinculando!.id),
    enabled: !!vinculando,
  });

  const accion = useMutation({
    mutationFn: (v: { id: string; accion: api.Accion }) => api.accion(v.id, v.accion),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cobros'] });
      setDescartando(null);
      setVinculando(null);
      setAnotando(null);
      setNota('');
      toast.success('Listo');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  // Baja lo que se está viendo (con los filtros puestos).
  const descargar = () => {
    const url = URL.createObjectURL(new Blob([api.aCsv(tickets)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `cobros-${estado.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const r = resumen.data;
  const todos = lista.data ?? [];
  const q = filtros.buscar.trim().toLowerCase();
  const tickets = todos.filter(
    (t) =>
      (!q || `${t.numero} ${t.cliente} ${t.telefono ?? ''} ${t.referencia ?? ''} ${t.ventaNumero ?? ''}`.toLowerCase().includes(q)) &&
      (!filtros.vendedora || (t.vendedora ?? '') === filtros.vendedora) &&
      (!filtros.origen || t.origen === filtros.origen) &&
      (!filtros.banco || (t.banco ?? '') === filtros.banco),
  );
  const facetas: Faceta[] = [
    { clave: 'estado', titulo: 'Estado', todas: 'Sin cerrar', opciones: PESTANAS.filter((p) => p.id !== 'ABIERTO').map((p) => ({ valor: p.id, etiqueta: p.etiqueta })) },
    { clave: 'periodo', titulo: 'Período', todas: 'Este mes', opciones: PERIODOS.filter((p) => p.id !== 'mes').map((p) => ({ valor: p.id, etiqueta: p.etiqueta })) },
    { clave: 'vendedora', titulo: 'Vendedora', opciones: opcionesDe(todos, (t) => t.vendedora) },
    { clave: 'origen', titulo: 'Origen', opciones: opcionesDe(todos, (t) => t.origen, (o) => api.ORIGEN[o as keyof typeof api.ORIGEN] ?? o) },
    { clave: 'banco', titulo: 'Banco', opciones: opcionesDe(todos, (t) => t.banco) },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Cobros"
        subtitulo="Cada pago o promesa que se vio en un chat, hasta que su venta se confirma. Todo lo que pasó, a la vista."
        acciones={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={descargar} disabled={!tickets.length}>
              <Download className="mr-1 h-4 w-4" /> Excel
            </Button>
            {esAdministracion && (
              <Button variant="outline" size="sm" onClick={() => setAjustes(true)} aria-label="Ajustes de cobros">
                <Settings2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        }
        kpis={
          supervisa &&
          r && (
            <>
              <Kpi titulo="Facturado" valor={dinero(r.facturado)} pie={r.ventas === 1 ? '1 venta confirmada' : `${numero(r.ventas)} ventas confirmadas`} tono="bueno" />
              <Kpi
                titulo="Comisión"
                valor={r.comision == null ? '—' : dinero(r.comision)}
                pie={r.comisionPorcentaje == null ? 'falta poner el %' : `${r.comisionPorcentaje} % de lo facturado`}
              />
              <Kpi
                titulo="Pagos vistos en chats"
                valor={numero(r.pagosVistos)}
                pie={r.montoVisto ? `${dinero(r.montoVisto)} leídos de comprobantes` : 'en el período'}
              />
              <Kpi
                titulo="Sin cerrar"
                valor={numero(r.abiertos)}
                pie={r.montoAbierto ? `${dinero(r.montoAbierto)} por confirmar` : `${numero(r.descartados)} descartados en el período`}
                tono={r.abiertos > 0 ? 'malo' : 'normal'}
              />
            </>
          )
        }
      />

      <Superfiltro
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholder="Buscar por ticket, cliente, referencia o venta…"
        facetas={facetas}
        valores={{ ...filtros, estado: estado === 'ABIERTO' ? undefined : estado, periodo: periodo === 'mes' ? undefined : periodo }}
        onCambiar={ponerVarios}
        onLimpiar={() => ponerVarios(sinFiltros(facetas))}
        resultados={lista.isLoading ? undefined : textoDeResultados(tickets.length, todos.length, ['ticket', 'tickets'])}
      />

      {lista.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : lista.isError ? (
        <div className="space-y-3 py-10 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudieron cargar los cobros.</p>
          <Button variant="outline" onClick={() => void lista.refetch()}>
            Reintentar
          </Button>
        </div>
      ) : todos.length === 0 ? (
        <EmptyState
          title={estado === 'ABIERTO' ? 'Nada sin cerrar' : 'Nada en este período'}
          description="Los tickets nacen solos cuando una clienta manda el comprobante, dice que pagó o queda en pasar a una hora."
        />
      ) : tickets.length === 0 ? (
        <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Ningún ticket coincide con la búsqueda o los filtros.
        </p>
      ) : (
        <ul className="space-y-2">
          {tickets.map((t) => {
            const productos = t.productosJson ?? [];
            const tarde = t.estado === 'ABIERTO' && t.avisos > 0;
            const visto = abierto === t.id;
            return (
              <li
                key={t.id}
                className={cn('rounded-lg border border-surface-border bg-surface p-3 sm:p-4', tarde && 'border-amber-300 dark:border-amber-800')}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      <span className="mr-2 font-mono text-xs text-slate-400">{t.numero}</span>
                      {t.cliente}
                    </p>
                    <p className="text-xs text-slate-500">
                      {[t.vendedora ?? 'Sin asignar', t.anuncioTitulo ? `Anuncio: ${t.anuncioTitulo}` : 'Sin anuncio', cuando(t.creadoAt)].join(' · ')}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="default">{api.ORIGEN[t.origen]}</Badge>
                    {t.montoDetectado != null && <Badge variant="default">{dinero(t.montoDetectado)}</Badge>}
                    {t.estado === 'CONFIRMADO' && (
                      <Badge variant="success">
                        {t.ventaNumero ?? 'Venta'} · {dinero(t.montoConfirmado)}
                      </Badge>
                    )}
                    {t.estado === 'DESCARTADO' && (
                      <Badge variant="danger">{api.MOTIVOS.find((m) => m.id === t.motivo)?.etiqueta ?? 'Descartado'}</Badge>
                    )}
                    {tarde && <Badge variant="warning">{t.avisos === 1 ? '1 aviso' : `${t.avisos} avisos`}</Badge>}
                  </div>
                </div>

                {t.evidencia && <p className="mt-2 break-words text-sm text-slate-700 dark:text-slate-300">«{t.evidencia}»</p>}
                {productos.length > 0 && (
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                    {productos.map((p) => `${p.cantidad}× ${p.nombre}${p.precio != null ? ` (${dinero(p.precio)})` : ''}${p.productId ? '' : ' — no está en el inventario'}`).join(' · ')}
                  </p>
                )}
                {t.faltaSaber && t.estado === 'ABIERTO' && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    {t.faltaSaber}
                    {t.respuesta ? ` · La dueña dijo: «${t.respuesta}»` : t.preguntadoAt ? ' · Se le preguntó a la dueña' : ''}
                  </p>
                )}
                {t.estado === 'ABIERTO' && t.revisarEl && new Date(t.revisarEl).getTime() > Date.now() && (
                  <p className="mt-1 text-xs text-slate-400">Se revisa el {cuando(t.revisarEl)} si no hay venta.</p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={`/conversations/${t.conversationId}`}
                    className="inline-flex h-[34px] items-center gap-1.5 rounded-lg border border-surface-border px-3.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> Abrir el chat
                  </Link>
                  {t.evidenciaUrl && (
                    <a
                      href={t.evidenciaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-[34px] items-center gap-1.5 rounded-lg border border-surface-border px-3.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <ImagenIcono className="h-3.5 w-3.5" /> Ver comprobante
                    </a>
                  )}
                  {t.estado === 'ABIERTO' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setVinculando(t)}>
                        <Link2 className="h-3.5 w-3.5" /> Ya se vendió
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setDescartando(t); setNota(''); }}>
                        <X className="h-3.5 w-3.5" /> Descartar
                      </Button>
                    </>
                  )}
                  {t.estado === 'DESCARTADO' && (
                    <Button variant="ghost" size="sm" loading={accion.isPending} onClick={() => accion.mutate({ id: t.id, accion: { tipo: 'reabrir' } })}>
                      <RotateCcw className="h-3.5 w-3.5" /> Reabrir
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => { setAnotando(t); setNota(''); }}>
                    <StickyNote className="h-3.5 w-3.5" /> Nota
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setAbierto(visto ? null : t.id)} aria-expanded={visto}>
                    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', visto && 'rotate-180')} /> Historial ({t.historial.length})
                  </Button>
                </div>

                {visto && (
                  <ol className="mt-3 space-y-1.5 border-l-2 border-slate-200 pl-3 text-xs dark:border-slate-700">
                    {t.historial.map((h, i) => (
                      <li key={i}>
                        <span className="text-slate-400">{cuando(h.at)} · </span>
                        <span className="font-medium text-slate-700 dark:text-slate-200">{h.quien}</span>
                        <span className="text-slate-600 dark:text-slate-400">: {h.que}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-xs text-slate-400">
        Un ticket se confirma solo cuando se confirma una venta de ese chat. Facturado es la suma de las ventas confirmadas del período; la
        comisión sale de ahí.
      </p>

      <Modal
        open={!!descartando}
        onClose={() => setDescartando(null)}
        title="Descartar"
        description={descartando ? `${descartando.numero} · ${descartando.cliente}` : undefined}
        footer={
          <>
            <Button variant="outline" onClick={() => setDescartando(null)}>
              Cancelar
            </Button>
            <Button
              loading={accion.isPending}
              onClick={() => descartando && accion.mutate({ id: descartando.id, accion: { tipo: 'descartar', motivo, nota: nota || null } })}
            >
              Descartar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid gap-1.5">
            {api.MOTIVOS.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm">
                <input type="radio" name="motivo" checked={motivo === m.id} onChange={() => setMotivo(m.id)} />
                {m.etiqueta}
              </label>
            ))}
          </div>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Qué pasó (opcional)"
            className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
        </div>
      </Modal>

      <Modal
        open={!!vinculando}
        onClose={() => setVinculando(null)}
        title="Ya se vendió"
        description="Elige la venta de este cliente. Si todavía no está, regístrala desde el chat: al confirmarla, el ticket se cierra solo."
      >
        {ventas.isLoading ? (
          <Spinner />
        ) : (ventas.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">Este cliente no tiene ventas confirmadas en los últimos 30 días.</p>
        ) : (
          <ul className="space-y-1.5">
            {(ventas.data ?? []).map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  disabled={accion.isPending}
                  onClick={() => vinculando && accion.mutate({ id: vinculando.id, accion: { tipo: 'vincular', saleId: v.id } })}
                  className="flex w-full items-center justify-between rounded-md border border-surface-border px-3 py-2 text-left text-sm hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                >
                  <span className="font-medium">{v.numero}</span>
                  <span className="text-slate-500">
                    {cuando(v.confirmedAt)} · {dinero(v.total)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <Modal
        open={!!anotando}
        onClose={() => setAnotando(null)}
        title="Nota"
        description={anotando ? `${anotando.numero} · ${anotando.cliente}` : undefined}
        footer={
          <>
            <Button variant="outline" onClick={() => setAnotando(null)}>
              Cancelar
            </Button>
            <Button
              loading={accion.isPending}
              disabled={!nota.trim()}
              onClick={() => anotando && accion.mutate({ id: anotando.id, accion: { tipo: 'nota', nota } })}
            >
              Guardar
            </Button>
          </>
        }
      >
        <textarea
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="Queda en el historial, con tu nombre y la hora"
          className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      </Modal>

      {ajustes && <Ajustes onCerrar={() => setAjustes(false)} />}
    </div>
  );
}
